import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Chip,
  Alert,
  AlertTitle,
  Grid // Для более удобного расположения информации о времени
} from '@mui/material';
import { CheckCircleOutline, HighlightOff, AccessTime, EventAvailable, EventNote, HourglassEmpty } from '@mui/icons-material'; // Добавим иконки
import TotalVisitDuration from './total-visit-duration'; 

export default function QualityChecklist({ visitInfo }) {

  if (!visitInfo) {
    return (
      <Box sx={{ mt: 2, p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 3 }}>
        <Alert severity="warning" variant="outlined">
          Информация о визите отсутствует.
        </Alert>
      </Box>
    );
  }

  if (visitInfo.maxQcType === 0) {
    return (
        <Box sx={{
          width: 300,
          mt: 2,
          p: { xs: 1.5, sm: 2, md: 3 },
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: 3,
        }}>
             <Typography variant="h6" component="div" sx={{
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
             }}>
              Контроль качества
            </Typography>
            <Alert severity="info" variant="outlined">
              Заполнение чеклиста не требуется.
            </Alert>
        </Box>
    );
  }

  // Основные данные из visitInfo
  const checklistItems = visitInfo.quality_control_checklist || [];
  const hasChecklistItems = checklistItems.length > 0;
  const isFinalized = visitInfo.is_quality_checked === true;

  const {
    first_interaction_date_formatted,
    finalized_date_formatted,
    acceptance_time_minutes,
    last_checklist_update // Дата последнего изменения, если не завершен
  } = visitInfo;

  console.log("Данные, переданные в TotalVisitDuration:", visitInfo);

  return (
    <Box sx={{
      width: 300,
      mt: 2,
      p: { xs: 1.5, sm: 2, md: 3 }, // Адаптивные отступы
      borderRadius: 2,
      bgcolor: 'background.paper',
      boxShadow: 3,
    }}>
    {/* Заголовок и статус */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" component="div" sx={{
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          Контроль качества
        </Typography>
        {isFinalized ? (
          <Chip icon={<CheckCircleOutline />} label="Проверено" color="success" size="small" variant="filled" />
        ) : (first_interaction_date_formatted || hasChecklistItems) ? ( // Если начато или есть пункты
          <Chip icon={<HourglassEmpty />} label="В процессе" color="warning" size="small" variant="filled" />
        ) : (
          <Chip label="Не начато" size="small" variant="outlined" />
        )}
      </Box>

      <TotalVisitDuration visitInfo={visitInfo} />

      {/* Информация о времени приемки */}
      <Box sx={{
        mb: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover', // Легкий фон для выделения блока
      }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.primary', mb: 1.5 }}>
          Детали приемки:
        </Typography>
        <Grid container spacing={1.5}>
          {first_interaction_date_formatted && (
            <Grid item xs={12} sm={isFinalized ? 6 : 12}> {/* Занимает всю ширину если не финализирован, иначе половину */}
                {/* <EventNote fontSize="small" color="action" sx={{ mr: 0.5 }} /> */}
                <EventNote fontSize="small" color="action" sx={{ display: 'block', mb: 0.5 }} />
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
                <strong>Начало:</strong> 
                <span>{first_interaction_date_formatted}</span>
                </Typography>
            </Grid>
          )}

          {isFinalized && finalized_date_formatted && (
            <Grid item xs={12} sm={6}>
              <EventAvailable fontSize="small" color="action" sx={{ display: 'block', mb: 0.5 }} />
                {/* <EventAvailable fontSize="small" color="action" sx={{ mr: 0.5 }} /> */}
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}> 
                <strong>Завершение:</strong>
                <span>{finalized_date_formatted}</span>
              </Typography>
            </Grid>
          )}
          
          {isFinalized && typeof acceptance_time_minutes === 'number' && (
             <Grid item xs={12}> {/* Продолжительность на всю ширину под датами */}
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}> 
                <AccessTime fontSize="small" color="action" />
                <strong>Продолжительность:</strong> {acceptance_time_minutes} мин.
              </Typography>
            </Grid>
          )}

          {!isFinalized && last_checklist_update && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime fontSize="small" color="action" />
                <strong>Последнее изменение:</strong> {last_checklist_update}
              </Typography>
            </Grid>
          )}
          
          {/* Если никакой информации о времени нет */}
          {!first_interaction_date_formatted && !isFinalized && !last_checklist_update && (
             <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                    Данные о времени приемки пока отсутствуют.
                </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Сам чеклист */}
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.primary', mb: 1 }}>
        Чеклист:
      </Typography>
      {hasChecklistItems ? (
        <List dense sx={{ pt: 0 }}>
          {checklistItems.map((item, index) => (
            <ListItem
              key={item.name || `cl-item-${index}`} // Используем name из конфига, если есть
              disablePadding
              sx={{
                py: 0.5,
                px: 1,
                mb: 0.5,
                borderRadius: 1,
                transition: 'background-color 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'action.selected' // Более выраженный ховер
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38, mr: 0.5 }}> {/* Уменьшил отступ */}
                <Checkbox
                  edge="start"
                  checked={!!item.checked} // Убедимся, что это boolean
                  tabIndex={-1} // Для отображения, не интерактивно
                  disableRipple
                  icon={<HighlightOff sx={{ color: 'error.light' }} />}
                  checkedIcon={<CheckCircleOutline sx={{ color: 'success.main' }} />}
                  sx={{ p: 0.25 }} // Уменьшил внутренний паддинг чекбокса
                />
              </ListItemIcon>
              <ListItemText
                primary={item.label || 'Пункт без описания'}
                primaryTypographyProps={{
                  variant: 'body1',
                  color: item.checked ? 'text.primary' : 'text.secondary',
                  sx: {
                    opacity: item.checked ? 1 : 0.75, // Немного увеличил opacity для непроверенных
                    fontWeight: item.checked ? 500 : 400, // Выделяем выполненные пункты
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          Пункты чеклиста отсутствуют или еще не были загружены.
        </Alert>
      )}
    </Box>
  );
}
